import { getApplicationInstance } from '../init/core/Application';
import createSwiperInstance from './createSwiperInstance';
import lockScroll from './lockScroll';

export default function activateSocialVideos(disableDragging = false) {
    const applicationInstance = getApplicationInstance();
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rows = Array.from(document.querySelectorAll<HTMLElement>('[data-social-video-rotation]'));
    const cleanupFunctions = rows.map((row, index) => activateSocialVideoRow(row, prefersReducedMotion, index, disableDragging));

    if (!cleanupFunctions.length) {
        return;
    }

    applicationInstance.addCleanupFn(() => {
        cleanupFunctions.forEach((cleanup) => cleanup());
    });
}

function activateSocialVideoRow(row: HTMLElement, prefersReducedMotion: boolean, index: number, disableDragging: boolean) {
    createSwiperInstance(
        row,
        {
            slidesPerView: 'auto',
            spaceBetween: 16,
            freeMode: true,
            allowTouchMove: !disableDragging,
            simulateTouch: !disableDragging,
            grabCursor: !disableDragging,
            watchOverflow: true,
            noSwiping: false,
            keyboard: { enabled: true },
            navigation: {
                prevEl: row.querySelector<HTMLElement>('[data-social-video-previous]'),
                nextEl: row.querySelector<HTMLElement>('[data-social-video-next]'),
            },
        },
        `socialContent${index}`
    );

    const cards = Array.from(row.querySelectorAll<HTMLElement>('[data-social-video-card]'));
    const videoCards = cards.filter((card) => card.querySelector('video[data-src]'));
    const openButtons = new Map<HTMLButtonElement, EventListener>();
    const hoverListeners = new Map<HTMLElement, { enter: EventListener; leave: EventListener }>();

    if (!cards.length) {
        return () => {};
    }

    let activeVideoIndex = 0;
    let rotation: number | undefined;
    let modalBackdrop: HTMLElement | null = null;

    const deactivateCard = (card: HTMLElement) => {
        const video = card.querySelector<HTMLVideoElement>('video[data-src]');
        card.classList.remove('is-active', 'is-ready');

        if (!video) return;

        video.pause();
        video.removeAttribute('src');
        video.load();
    };

    const activateCard = (index: number) => {
        cards.forEach(deactivateCard);
        if (!videoCards.length) return;

        activeVideoIndex = index % videoCards.length;

        const card = videoCards[activeVideoIndex];
        const video = card.querySelector<HTMLVideoElement>('video[data-src]');
        const source = video?.dataset.src;

        if (!video || !source) return;

        card.classList.add('is-active');
        video.addEventListener('loadeddata', () => {
            if (card.classList.contains('is-active') && video.getAttribute('src') === source) {
                card.classList.add('is-ready');
            }
        }, { once: true });
        video.src = source;
        video.currentTime = 0;
        void video.play().catch(() => deactivateCard(card));
    };

    const stopRotation = () => {
        if (rotation === undefined) return;

        window.clearInterval(rotation);
        rotation = undefined;
    };

    const startRotation = () => {
        stopRotation();
        if (!prefersReducedMotion && videoCards.length > 1) {
            rotation = window.setInterval(() => activateCard(activeVideoIndex + 1), 5000);
        }
    };

    const closeModal = (resumeRotation = true) => {
        if (!modalBackdrop) return;

        const modalVideo = modalBackdrop.querySelector<HTMLVideoElement>('video');
        modalVideo?.pause();
        modalVideo?.removeAttribute('src');
        modalVideo?.load();
        modalBackdrop.remove();
        modalBackdrop = null;
        document.removeEventListener('keydown', onModalKeydown);
        lockScroll(false);

        if (resumeRotation) {
            activateCard(activeVideoIndex);
            startRotation();
        }
    };

    const onModalKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') closeModal();
    };

    const openModal = (card: HTMLElement) => {
        if (modalBackdrop) return;

        const videoIndex = videoCards.indexOf(card);
        if (videoIndex >= 0) activeVideoIndex = videoIndex;
        stopRotation();
        cards.forEach(deactivateCard);

        const backdrop = document.createElement('div');
        backdrop.className = 'homepage-social-video-modal';
        backdrop.setAttribute('role', 'dialog');
        backdrop.setAttribute('aria-modal', 'true');
        backdrop.setAttribute('aria-label', 'Social content viewer');

        const modalCard = card.cloneNode(true) as HTMLElement;
        modalCard.classList.remove('is-ready');
        modalCard.classList.add('homepage-social-video-card--modal', 'is-active');
        modalCard.querySelector('[data-social-video-open]')?.remove();

        const closeButton = document.createElement('button');
        closeButton.className = 'homepage-social-video-modal__close';
        closeButton.type = 'button';
        closeButton.setAttribute('aria-label', 'Close social content');
        closeButton.textContent = '\u00d7';

        const modalVideo = modalCard.querySelector<HTMLVideoElement>('video[data-src]');
        const source = modalVideo?.dataset.src;
        if (modalVideo && source) {
            modalVideo.addEventListener('loadeddata', () => modalCard.classList.add('is-ready'), { once: true });
            modalVideo.muted = false;
            modalVideo.removeAttribute('muted');
            modalVideo.controls = true;
            modalVideo.src = source;
        }

        backdrop.append(modalCard, closeButton);
        document.body.append(backdrop);
        modalBackdrop = backdrop;
        lockScroll(true);
        closeButton.focus();

        backdrop.addEventListener('click', (event) => {
            if (event.target === backdrop) closeModal();
        });
        closeButton.addEventListener('click', () => closeModal());
        document.addEventListener('keydown', onModalKeydown);
        void modalVideo?.play();
    };

    cards.forEach((card) => {
        const button = card.querySelector<HTMLButtonElement>('[data-social-video-open]');
        if (!button) return;

        const listener = () => openModal(card);
        button.addEventListener('click', listener);
        openButtons.set(button, listener);
    });

    videoCards.forEach((card, index) => {
        const enter = () => {
            stopRotation();
            activateCard(index);
        };
        const leave = () => startRotation();

        card.addEventListener('pointerenter', enter);
        card.addEventListener('pointerleave', leave);
        hoverListeners.set(card, { enter, leave });
    });

    if (videoCards.length) activateCard(0);
    startRotation();

    return () => {
        stopRotation();
        closeModal(false);
        cards.forEach(deactivateCard);
        openButtons.forEach((listener, button) => button.removeEventListener('click', listener));
        hoverListeners.forEach(({ enter, leave }, card) => {
            card.removeEventListener('pointerenter', enter);
            card.removeEventListener('pointerleave', leave);
        });
    };
}
